import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { format, differenceInMinutes } from 'date-fns'

const getLogoBase64 = (): Promise<string> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve('')
      return
    }
    const img = new Image()
    img.src = '/logo_sfundo.png'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } else resolve('')
    }
    img.onerror = () => resolve('')
  })
}

interface TimesheetRecord {
  date: string
  entrada: string | null
  saida_almoco: string | null
  volta_almoco: string | null
  saida: string | null
  total_horas: string | null
  observacoes?: string | null
}

interface EmployeeInfo {
  full_name: string
  cpf: string | null
  phone: string | null
  email: string
  role: string
}

interface CompanyInfo {
  name: string
  address?: string
}

const formatTime = (timestamp: string | null) => {
  if (!timestamp) return '-'
  const date = new Date(timestamp)
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const formatCPF = (cpf: string | null) => {
  if (!cpf) return 'Não informado'
  const cleaned = cpf.replace(/\D/g, '')
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

const formatPhone = (phone: string | null) => {
  if (!phone) return 'Não informado'
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }
  return phone
}

const generateVerificationCode = (employeeId: string, startDate: string, endDate: string) => {
  const data = `${employeeId}-${startDate}-${endDate}`
  return btoa(data).substring(0, 12).toUpperCase()
}

const parseInterval = (interval: string | null): number => {
  if (!interval) return 0
  
  // Format: "HH:MM:SS" or "X hours Y minutes"
  if (interval.includes('seconds')) {
    const seconds = parseFloat(interval.split(' ')[0])
    return seconds / 3600 // Convert to hours
  }
  
  const parts = interval.split(':')
  if (parts.length === 3) {
    const hours = parseInt(parts[0])
    const minutes = parseInt(parts[1])
    return hours + (minutes / 60)
  }
  
  return 0
}

const formatHoursMinutes = (hours: number) => {
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  
  if (minutes === 0) {
    return `${wholeHours}h`
  }
  return `${wholeHours}h ${minutes}m`
}

// Financial Report Interfaces
interface FinancialSummaryData {
  condominiumName: string
  condominiumAddress?: string
  startDate: string
  endDate: string
  totalRevenues: number
  totalExpenses: number
  balance: number
  chargesStats: { paid: number; pending: number; overdue: number; canceled: number }
  expensesByCategory: Array<{ category: string; amount: number }>
  revenuesByCategory: Array<{ category: string; amount: number }>
  topSuppliers: Array<{ name: string; total: number }>
}

interface FinancialDetailedData {
  condominiumName: string
  condominiumAddress?: string
  startDate: string
  endDate: string
  revenues: Array<{
    date: string
    description: string
    category: string
    amount: number
    payment_method?: string
  }>
  expenses: Array<{
    date: string
    description: string
    category: string
    supplier?: string
    amount: number
    status: string
  }>
  charges: Array<{
    unit: string
    resident?: string
    description: string
    due_date: string
    amount: number
    status: string
  }>
}

interface CashFlowData {
  condominiumName: string
  condominiumAddress?: string
  startDate: string
  endDate: string
  initialBalance: number
  revenues: Array<{ date: string; description: string; amount: number }>
  expenses: Array<{ date: string; description: string; amount: number }>
  dailyBalance: Array<{ date: string; inflow: number; outflow: number; balance: number }>
  finalBalance: number
}

interface DefaultReportData {
  condominiumName: string
  condominiumAddress?: string
  reportDate: string
  overdueCharges: Array<{
    unit: string
    resident?: string
    description: string
    due_date: string
    days_overdue: number
    amount: number
  }>
  totalOverdue: number
  totalCharges: number
  defaultRate: number
}

interface SupplierReportData {
  condominiumName: string
  condominiumAddress?: string
  startDate: string
  endDate: string
  supplier: {
    name: string
    document?: string
    category?: string
    phone?: string
    email?: string
  }
  expenses: Array<{
    date: string
    description: string
    amount: number
    status: string
  }>
  totalPaid: number
  totalPending: number
  serviceCount: number
  averageTicket: number
}

interface ChargesReportData {
  condominiumName: string
  condominiumAddress?: string
  startDate: string
  endDate: string
  charges: Array<{
    unit: string
    resident?: string
    description: string
    due_date: string
    amount: number
    status: string
    paid_date?: string
  }>
  stats: { paid: number; pending: number; overdue: number; canceled: number }
  collectionRate: number
}

// Export existing function
export const generateTimesheetPDF = async (
  records: TimesheetRecord[],
  employeeInfo: EmployeeInfo,
  companyInfo: CompanyInfo,
  startDate: string,
  endDate: string
) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark - ITACONDO SISTEMAS
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, {
    angle: 45,
    align: 'center',
  })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, {
    angle: 45,
    align: 'center',
  })
  doc.restoreGraphicsState()
  
  // Header - Logo and Title
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('FOLHA DE PONTO INDIVIDUAL', pageWidth - 15, 18, { align: 'right' })
  
  // Line separator
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  // Company Information
  let yPosition = 40
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('EMPRESA:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(companyInfo.name, 45, yPosition)
  
  if (companyInfo.address) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(companyInfo.address, 45, yPosition)
  }
  
  // Employee Information
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('FUNCIONÁRIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(employeeInfo.full_name, 50, yPosition)
  
  yPosition += 6
  doc.setFont('helvetica', 'bold')
  doc.text('CPF:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(formatCPF(employeeInfo.cpf), 30, yPosition)
  
  yPosition += 6
  doc.setFont('helvetica', 'bold')
  doc.text('Telefone:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(formatPhone(employeeInfo.phone), 40, yPosition)
  
  yPosition += 6
  doc.setFont('helvetica', 'bold')
  doc.text('Email:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(employeeInfo.email, 35, yPosition)
  
  yPosition += 6
  doc.setFont('helvetica', 'bold')
  doc.text('Cargo:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(employeeInfo.role === 'porteiro' ? 'Porteiro' : 'Operador', 35, yPosition)
  
  // Period
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('PERÍODO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  const formattedStartDate = new Date(startDate).toLocaleDateString('pt-BR')
  const formattedEndDate = new Date(endDate).toLocaleDateString('pt-BR')
  doc.text(`${formattedStartDate} a ${formattedEndDate}`, 45, yPosition)
  
  // Line separator
  yPosition += 5
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, pageWidth - 15, yPosition)
  
  // Table title
  yPosition += 8
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('REGISTROS DE PONTO', pageWidth / 2, yPosition, { align: 'center' })
  
  // Prepare table data
  const tableData = records.map(record => [
    new Date(record.date).toLocaleDateString('pt-BR'),
    formatTime(record.entrada),
    formatTime(record.saida_almoco),
    formatTime(record.volta_almoco),
    formatTime(record.saida),
    record.total_horas ? formatHoursMinutes(parseInterval(record.total_horas)) : '-',
    record.observacoes || '-'
  ])
  
  // Calculate total hours
  const totalHours = records.reduce((sum, record) => {
    return sum + parseInterval(record.total_horas)
  }, 0)
  
  // Generate table
  autoTable(doc, {
    startY: yPosition + 5,
    head: [['Data', 'Entrada', 'Saída Almoço', 'Volta Almoço', 'Saída', 'Total Horas', 'Observações']],
    body: tableData,
    foot: [['', '', '', '', 'TOTAL:', formatHoursMinutes(totalHours), '']],
    theme: 'striped',
    headStyles: {
      fillColor: [66, 135, 245],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    footStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 8,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 20 },
      2: { cellWidth: 25 },
      3: { cellWidth: 25 },
      4: { cellWidth: 20 },
      5: { cellWidth: 25 },
      6: { cellWidth: 'auto' }
    }
  })
  
  // Signatures section
  const finalY = (doc as any).lastAutoTable.finalY + 20
  
  if (finalY < pageHeight - 60) {
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    
    const signatureY = finalY + 10
    const leftX = 40
    const rightX = pageWidth - 80
    
    // Signature lines
    doc.line(leftX, signatureY, leftX + 60, signatureY)
    doc.line(rightX, signatureY, rightX + 60, signatureY)
    
    // Labels
    doc.text('Funcionário', leftX + 15, signatureY + 5)
    doc.text('Gerente', rightX + 20, signatureY + 5)
  }
  
  // Footer with security information
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(employeeInfo.email, startDate, endDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  // Save PDF
  const fileName = `folha-ponto-${employeeInfo.full_name.replace(/\s+/g, '-').toLowerCase()}-${startDate}-${endDate}.pdf`
  doc.save(fileName)
}

// Financial Report PDF Generators

export const generateFinancialSummaryPDF = async (data: FinancialSummaryData) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, { angle: 45, align: 'center' })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, { angle: 45, align: 'center' })
  doc.restoreGraphicsState()
  
  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('RESUMO EXECUTIVO FINANCEIRO', pageWidth - 15, 18, { align: 'right' })
  
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  let yPosition = 40
  
  // Company info
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('CONDOMÍNIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.condominiumName, 50, yPosition)
  
  if (data.condominiumAddress) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.condominiumAddress, 50, yPosition)
  }
  
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('PERÍODO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  const formattedStart = new Date(data.startDate).toLocaleDateString('pt-BR')
  const formattedEnd = new Date(data.endDate).toLocaleDateString('pt-BR')
  doc.text(`${formattedStart} a ${formattedEnd}`, 45, yPosition)
  
  yPosition += 10
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, pageWidth - 15, yPosition)
  
  // KPIs Section
  yPosition += 10
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('INDICADORES PRINCIPAIS', pageWidth / 2, yPosition, { align: 'center' })
  
  yPosition += 10
  const kpiData = [
    ['Total de Receitas', `R$ ${(data.totalRevenues / 100).toFixed(2)}`],
    ['Total de Despesas', `R$ ${(data.totalExpenses / 100).toFixed(2)}`],
    ['Saldo do Período', `R$ ${(data.balance / 100).toFixed(2)}`]
  ]
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Indicador', 'Valor']],
    body: kpiData,
    theme: 'striped',
    headStyles: { fillColor: [66, 135, 245], fontStyle: 'bold' },
    styles: { fontSize: 11, cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: 'auto', halign: 'right', fontStyle: 'bold' } }
  })
  
  yPosition = (doc as any).lastAutoTable.finalY + 10
  
  // Charges Status
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('STATUS DAS COBRANÇAS', 15, yPosition)
  
  yPosition += 5
  const chargesData = [
    ['Pagas', `R$ ${(data.chargesStats.paid / 100).toFixed(2)}`],
    ['Pendentes', `R$ ${(data.chargesStats.pending / 100).toFixed(2)}`],
    ['Vencidas', `R$ ${(data.chargesStats.overdue / 100).toFixed(2)}`],
    ['Canceladas', `R$ ${(data.chargesStats.canceled / 100).toFixed(2)}`]
  ]
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Status', 'Valor']],
    body: chargesData,
    theme: 'grid',
    headStyles: { fillColor: [66, 135, 245] },
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: { 1: { halign: 'right' } }
  })
  
  yPosition = (doc as any).lastAutoTable.finalY + 10
  
  // Expenses by Category
  if (data.expensesByCategory.length > 0) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('DESPESAS POR CATEGORIA', 15, yPosition)
    
    yPosition += 5
    const expensesData = data.expensesByCategory.map(e => [
      e.category,
      `R$ ${(e.amount / 100).toFixed(2)}`
    ])
    
    autoTable(doc, {
      startY: yPosition,
      head: [['Categoria', 'Valor']],
      body: expensesData,
      theme: 'striped',
      headStyles: { fillColor: [220, 53, 69] },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: 'right' } }
    })
    
    yPosition = (doc as any).lastAutoTable.finalY + 10
  }
  
  // Top Suppliers
  if (data.topSuppliers.length > 0 && yPosition < pageHeight - 60) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('PRINCIPAIS FORNECEDORES', 15, yPosition)
    
    yPosition += 5
    const suppliersData = data.topSuppliers.map(s => [
      s.name,
      `R$ ${(s.total / 100).toFixed(2)}`
    ])
    
    autoTable(doc, {
      startY: yPosition,
      head: [['Fornecedor', 'Total']],
      body: suppliersData,
      theme: 'striped',
      headStyles: { fillColor: [40, 167, 69] },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: 'right' } }
    })
  }
  
  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(data.condominiumName, data.startDate, data.endDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  const fileName = `relatorio-financeiro-resumo-${data.startDate}-${data.endDate}.pdf`
  doc.save(fileName)
}

export const generateFinancialDetailedPDF = async (data: FinancialDetailedData) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, { angle: 45, align: 'center' })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, { angle: 45, align: 'center' })
  doc.restoreGraphicsState()
  
  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('RELATÓRIO FINANCEIRO ANALÍTICO', pageWidth - 15, 18, { align: 'right' })
  
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  let yPosition = 40
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('CONDOMÍNIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.condominiumName, 50, yPosition)
  
  if (data.condominiumAddress) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.condominiumAddress, 50, yPosition)
  }
  
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('PERÍODO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  const formattedStart = new Date(data.startDate).toLocaleDateString('pt-BR')
  const formattedEnd = new Date(data.endDate).toLocaleDateString('pt-BR')
  doc.text(`${formattedStart} a ${formattedEnd}`, 45, yPosition)
  
  yPosition += 10
  
  // Revenues Section
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('RECEITAS', pageWidth / 2, yPosition, { align: 'center' })
  
  if (data.revenues.length > 0) {
    const revenuesData = data.revenues.map(r => [
      new Date(r.date).toLocaleDateString('pt-BR'),
      r.description,
      r.category,
      `R$ ${(r.amount / 100).toFixed(2)}`
    ])
    
    const totalRevenues = data.revenues.reduce((sum, r) => sum + r.amount, 0)
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Data', 'Descrição', 'Categoria', 'Valor']],
      body: revenuesData,
      foot: [['', '', 'TOTAL:', `R$ ${(totalRevenues / 100).toFixed(2)}`]],
      theme: 'striped',
      headStyles: { fillColor: [40, 167, 69], fontSize: 9 },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 3: { halign: 'right' } }
    })
    
    yPosition = (doc as any).lastAutoTable.finalY + 15
  }
  
  // Expenses Section
  if (yPosition > pageHeight - 80) {
    doc.addPage()
    yPosition = 20
  }
  
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('DESPESAS', pageWidth / 2, yPosition, { align: 'center' })
  
  if (data.expenses.length > 0) {
    const expensesData = data.expenses.map(e => [
      new Date(e.date).toLocaleDateString('pt-BR'),
      e.description,
      e.supplier || '-',
      e.category,
      e.status,
      `R$ ${(e.amount / 100).toFixed(2)}`
    ])
    
    const totalExpenses = data.expenses.reduce((sum, e) => sum + e.amount, 0)
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Data', 'Descrição', 'Fornecedor', 'Categoria', 'Status', 'Valor']],
      body: expensesData,
      foot: [['', '', '', '', 'TOTAL:', `R$ ${(totalExpenses / 100).toFixed(2)}`]],
      theme: 'striped',
      headStyles: { fillColor: [220, 53, 69], fontSize: 8 },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 5: { halign: 'right' } }
    })
    
    yPosition = (doc as any).lastAutoTable.finalY + 15
  }
  
  // Charges Section
  if (yPosition > pageHeight - 80) {
    doc.addPage()
    yPosition = 20
  }
  
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('COBRANÇAS', pageWidth / 2, yPosition, { align: 'center' })
  
  if (data.charges.length > 0) {
    const chargesData = data.charges.map(c => [
      c.unit,
      c.resident || '-',
      c.description,
      new Date(c.due_date).toLocaleDateString('pt-BR'),
      c.status,
      `R$ ${(c.amount / 100).toFixed(2)}`
    ])
    
    const totalCharges = data.charges.reduce((sum, c) => sum + c.amount, 0)
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Unidade', 'Morador', 'Descrição', 'Vencimento', 'Status', 'Valor']],
      body: chargesData,
      foot: [['', '', '', '', 'TOTAL:', `R$ ${(totalCharges / 100).toFixed(2)}`]],
      theme: 'striped',
      headStyles: { fillColor: [66, 135, 245], fontSize: 8 },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 5: { halign: 'right' } }
    })
  }
  
  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(data.condominiumName, data.startDate, data.endDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  const fileName = `relatorio-financeiro-analitico-${data.startDate}-${data.endDate}.pdf`
  doc.save(fileName)
}

export const generateCashFlowPDF = async (data: CashFlowData) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, { angle: 45, align: 'center' })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, { angle: 45, align: 'center' })
  doc.restoreGraphicsState()
  
  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('RELATÓRIO DE FLUXO DE CAIXA', pageWidth - 15, 18, { align: 'right' })
  
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  let yPosition = 40
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('CONDOMÍNIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.condominiumName, 50, yPosition)
  
  if (data.condominiumAddress) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.condominiumAddress, 50, yPosition)
  }
  
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('PERÍODO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  const formattedStart = new Date(data.startDate).toLocaleDateString('pt-BR')
  const formattedEnd = new Date(data.endDate).toLocaleDateString('pt-BR')
  doc.text(`${formattedStart} a ${formattedEnd}`, 45, yPosition)
  
  yPosition += 10
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, pageWidth - 15, yPosition)
  
  // Balance summary
  yPosition += 10
  const summaryData = [
    ['Saldo Inicial', `R$ ${(data.initialBalance / 100).toFixed(2)}`],
    ['Total de Entradas', `R$ ${(data.revenues.reduce((s, r) => s + r.amount, 0) / 100).toFixed(2)}`],
    ['Total de Saídas', `R$ ${(data.expenses.reduce((s, e) => s + e.amount, 0) / 100).toFixed(2)}`],
    ['Saldo Final', `R$ ${(data.finalBalance / 100).toFixed(2)}`]
  ]
  
  autoTable(doc, {
    startY: yPosition,
    body: summaryData,
    theme: 'grid',
    styles: { fontSize: 11, cellPadding: 4, fontStyle: 'bold' },
    columnStyles: { 
      0: { cellWidth: 100, fillColor: [240, 240, 240] },
      1: { halign: 'right', fillColor: [255, 255, 255] }
    }
  })
  
  yPosition = (doc as any).lastAutoTable.finalY + 15
  
  // Daily cash flow
  if (data.dailyBalance.length > 0) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('MOVIMENTAÇÃO DIÁRIA', pageWidth / 2, yPosition, { align: 'center' })
    
    const dailyData = data.dailyBalance.map(d => [
      new Date(d.date).toLocaleDateString('pt-BR'),
      `R$ ${(d.inflow / 100).toFixed(2)}`,
      `R$ ${(d.outflow / 100).toFixed(2)}`,
      `R$ ${(d.balance / 100).toFixed(2)}`
    ])
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Data', 'Entradas', 'Saídas', 'Saldo']],
      body: dailyData,
      theme: 'striped',
      headStyles: { fillColor: [66, 135, 245] },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        1: { halign: 'right', textColor: [40, 167, 69] },
        2: { halign: 'right', textColor: [220, 53, 69] },
        3: { halign: 'right', fontStyle: 'bold' }
      }
    })
  }
  
  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(data.condominiumName, data.startDate, data.endDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  const fileName = `relatorio-fluxo-caixa-${data.startDate}-${data.endDate}.pdf`
  doc.save(fileName)
}

export const generateDefaultReportPDF = async (data: DefaultReportData) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, { angle: 45, align: 'center' })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, { angle: 45, align: 'center' })
  doc.restoreGraphicsState()
  
  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('RELATÓRIO DE INADIMPLÊNCIA', pageWidth - 15, 18, { align: 'right' })
  
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  let yPosition = 40
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('CONDOMÍNIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.condominiumName, 50, yPosition)
  
  if (data.condominiumAddress) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.condominiumAddress, 50, yPosition)
  }
  
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('DATA DO RELATÓRIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(new Date(data.reportDate).toLocaleDateString('pt-BR'), 65, yPosition)
  
  yPosition += 10
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, pageWidth - 15, yPosition)
  
  // Summary
  yPosition += 10
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('RESUMO DA INADIMPLÊNCIA', pageWidth / 2, yPosition, { align: 'center' })
  
  yPosition += 5
  const summaryData = [
    ['Total de Cobranças', `R$ ${(data.totalCharges / 100).toFixed(2)}`],
    ['Valor em Atraso', `R$ ${(data.totalOverdue / 100).toFixed(2)}`],
    ['Taxa de Inadimplência', `${data.defaultRate.toFixed(2)}%`],
    ['Quantidade de Débitos', data.overdueCharges.length.toString()]
  ]
  
  autoTable(doc, {
    startY: yPosition,
    body: summaryData,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 100, fillColor: [240, 240, 240] },
      1: { halign: 'right', textColor: [220, 53, 69] }
    }
  })
  
  yPosition = (doc as any).lastAutoTable.finalY + 15
  
  // Overdue charges
  if (data.overdueCharges.length > 0) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('DÉBITOS EM ATRASO', pageWidth / 2, yPosition, { align: 'center' })
    
    const overdueData = data.overdueCharges.map(c => [
      c.unit,
      c.resident || '-',
      c.description,
      new Date(c.due_date).toLocaleDateString('pt-BR'),
      c.days_overdue.toString(),
      `R$ ${(c.amount / 100).toFixed(2)}`
    ])
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Unidade', 'Morador', 'Descrição', 'Vencimento', 'Dias', 'Valor']],
      body: overdueData,
      foot: [['', '', '', '', 'TOTAL:', `R$ ${(data.totalOverdue / 100).toFixed(2)}`]],
      theme: 'striped',
      headStyles: { fillColor: [220, 53, 69] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [220, 53, 69] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        4: { halign: 'center' },
        5: { halign: 'right', textColor: [220, 53, 69] }
      }
    })
  }
  
  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(data.condominiumName, data.reportDate, data.reportDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  const fileName = `relatorio-inadimplencia-${data.reportDate}.pdf`
  doc.save(fileName)
}

export const generateSupplierReportPDF = async (data: SupplierReportData) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, { angle: 45, align: 'center' })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, { angle: 45, align: 'center' })
  doc.restoreGraphicsState()
  
  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('RELATÓRIO POR FORNECEDOR', pageWidth - 15, 18, { align: 'right' })
  
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  let yPosition = 40
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('CONDOMÍNIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.condominiumName, 50, yPosition)
  
  if (data.condominiumAddress) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.condominiumAddress, 50, yPosition)
  }
  
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('PERÍODO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  const formattedStart = new Date(data.startDate).toLocaleDateString('pt-BR')
  const formattedEnd = new Date(data.endDate).toLocaleDateString('pt-BR')
  doc.text(`${formattedStart} a ${formattedEnd}`, 45, yPosition)
  
  yPosition += 10
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, pageWidth - 15, yPosition)
  
  // Supplier info
  yPosition += 10
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('DADOS DO FORNECEDOR', pageWidth / 2, yPosition, { align: 'center' })
  
  yPosition += 8
  doc.setFontSize(10)
  doc.text('Nome:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.supplier.name, 40, yPosition)
  
  if (data.supplier.document) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('CNPJ/CPF:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.supplier.document, 45, yPosition)
  }
  
  if (data.supplier.category) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Categoria:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.supplier.category, 45, yPosition)
  }
  
  if (data.supplier.phone) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Telefone:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.supplier.phone, 45, yPosition)
  }
  
  if (data.supplier.email) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Email:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.supplier.email, 40, yPosition)
  }
  
  yPosition += 10
  
  // Statistics
  const statsData = [
    ['Total Pago', `R$ ${(data.totalPaid / 100).toFixed(2)}`],
    ['Total Pendente', `R$ ${(data.totalPending / 100).toFixed(2)}`],
    ['Número de Serviços', data.serviceCount.toString()],
    ['Ticket Médio', `R$ ${(data.averageTicket / 100).toFixed(2)}`]
  ]
  
  autoTable(doc, {
    startY: yPosition,
    body: statsData,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 100, fillColor: [240, 240, 240] },
      1: { halign: 'right' }
    }
  })
  
  yPosition = (doc as any).lastAutoTable.finalY + 15
  
  // Expenses list
  if (data.expenses.length > 0) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('HISTÓRICO DE DESPESAS', pageWidth / 2, yPosition, { align: 'center' })
    
    const expensesData = data.expenses.map(e => [
      new Date(e.date).toLocaleDateString('pt-BR'),
      e.description,
      e.status,
      `R$ ${(e.amount / 100).toFixed(2)}`
    ])
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Data', 'Descrição', 'Status', 'Valor']],
      body: expensesData,
      foot: [['', '', 'TOTAL:', `R$ ${((data.totalPaid + data.totalPending) / 100).toFixed(2)}`]],
      theme: 'striped',
      headStyles: { fillColor: [40, 167, 69] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 3: { halign: 'right' } }
    })
  }
  
  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(data.supplier.name, data.startDate, data.endDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  const fileName = `relatorio-fornecedor-${data.supplier.name.replace(/\s+/g, '-').toLowerCase()}-${data.startDate}-${data.endDate}.pdf`
  doc.save(fileName)
}

export const generateChargesReportPDF = async (data: ChargesReportData) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  
  // Watermark
  doc.setTextColor(220, 220, 220)
  doc.setFontSize(60)
  doc.setFont('helvetica', 'bold')
  doc.saveGraphicsState()
  doc.text('ITACONDO', pageWidth / 2, pageHeight / 2, { angle: 45, align: 'center' })
  doc.text('SISTEMAS', pageWidth / 2, (pageHeight / 2) + 20, { angle: 45, align: 'center' })
  doc.restoreGraphicsState()
  
  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 20)
  }
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(80, 80, 80)
  doc.text('RELATÓRIO DE COBRANÇAS', pageWidth - 15, 18, { align: 'right' })
  
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 30, pageWidth - 15, 30)
  
  let yPosition = 40
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text('CONDOMÍNIO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  doc.text(data.condominiumName, 50, yPosition)
  
  if (data.condominiumAddress) {
    yPosition += 6
    doc.setFont('helvetica', 'bold')
    doc.text('Endereço:', 15, yPosition)
    doc.setFont('helvetica', 'normal')
    doc.text(data.condominiumAddress, 50, yPosition)
  }
  
  yPosition += 10
  doc.setFont('helvetica', 'bold')
  doc.text('PERÍODO:', 15, yPosition)
  doc.setFont('helvetica', 'normal')
  const formattedStart = new Date(data.startDate).toLocaleDateString('pt-BR')
  const formattedEnd = new Date(data.endDate).toLocaleDateString('pt-BR')
  doc.text(`${formattedStart} a ${formattedEnd}`, 45, yPosition)
  
  yPosition += 10
  doc.setLineWidth(0.5)
  doc.line(15, yPosition, pageWidth - 15, yPosition)
  
  // Summary
  yPosition += 10
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.text('RESUMO DE COBRANÇAS', pageWidth / 2, yPosition, { align: 'center' })
  
  yPosition += 5
  const totalAmount = data.stats.paid + data.stats.pending + data.stats.overdue + data.stats.canceled
  const summaryData = [
    ['Pagas', `R$ ${(data.stats.paid / 100).toFixed(2)}`],
    ['Pendentes', `R$ ${(data.stats.pending / 100).toFixed(2)}`],
    ['Vencidas', `R$ ${(data.stats.overdue / 100).toFixed(2)}`],
    ['Canceladas', `R$ ${(data.stats.canceled / 100).toFixed(2)}`],
    ['Total Geral', `R$ ${(totalAmount / 100).toFixed(2)}`],
    ['Taxa de Recebimento', `${data.collectionRate.toFixed(2)}%`]
  ]
  
  autoTable(doc, {
    startY: yPosition,
    body: summaryData,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 100, fillColor: [240, 240, 240] },
      1: { halign: 'right' }
    }
  })
  
  yPosition = (doc as any).lastAutoTable.finalY + 15
  
  // Charges list
  if (data.charges.length > 0) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('DETALHAMENTO DAS COBRANÇAS', pageWidth / 2, yPosition, { align: 'center' })
    
    const chargesData = data.charges.map(c => [
      c.unit,
      c.resident || '-',
      c.description,
      new Date(c.due_date).toLocaleDateString('pt-BR'),
      c.status,
      c.paid_date ? new Date(c.paid_date).toLocaleDateString('pt-BR') : '-',
      `R$ ${(c.amount / 100).toFixed(2)}`
    ])
    
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Unidade', 'Morador', 'Descrição', 'Vencimento', 'Status', 'Pagamento', 'Valor']],
      body: chargesData,
      foot: [['', '', '', '', '', 'TOTAL:', `R$ ${(totalAmount / 100).toFixed(2)}`]],
      theme: 'striped',
      headStyles: { fillColor: [66, 135, 245], fontSize: 7 },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 6: { halign: 'right' } }
    })
  }
  
  // Footer
  const footerY = pageHeight - 25
  doc.setLineWidth(0.5)
  doc.line(15, footerY - 5, pageWidth - 15, footerY - 5)
  
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Este documento foi gerado eletronicamente pelo sistema itaCondo', pageWidth / 2, footerY, { align: 'center' })
  
  const now = new Date()
  const generationDate = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
  doc.text(`Data de emissão: ${generationDate}`, pageWidth / 2, footerY + 5, { align: 'center' })
  
  const verificationCode = generateVerificationCode(data.condominiumName, data.startDate, data.endDate)
  doc.setFont('helvetica', 'bold')
  doc.text(`Código de Verificação: ${verificationCode}`, pageWidth / 2, footerY + 10, { align: 'center' })
  
  const fileName = `relatorio-cobrancas-${data.startDate}-${data.endDate}.pdf`
  doc.save(fileName)
}

interface VisitorReportData {
  name: string
  document: string | null
  destination: string | null
  entry_time: string | null
  exit_time: string | null
  created_at: string | null
  status: string | null
  registered_by?: string | null
}

export const generateVisitorReportPDF = async (
  visitors: VisitorReportData[],
  companyInfo: { name: string; address?: string },
  startDate: string,
  endDate: string
) => {
  const doc = new jsPDF('landscape')
  const pageWidth = doc.internal.pageSize.getWidth()

  // Header
  const logo = await getLogoBase64()
  if (logo) {
    doc.addImage(logo, 'PNG', 15, 10, 38, 12)
  } else {
    doc.setTextColor(30, 58, 138)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('ITACONDO SISTEMAS', 15, 18)
  }
  
  doc.setTextColor(0, 0, 0)
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('Relatório de Visitantes', pageWidth - 15, 16, { align: 'right' })
  
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  doc.text(`Período: ${format(new Date(startDate), 'dd/MM/yyyy')} a ${format(new Date(endDate), 'dd/MM/yyyy')}`, pageWidth - 15, 23, { align: 'right' })
  doc.text(companyInfo.name, pageWidth - 15, 29, { align: 'right' })

  // Line separator
  doc.setLineWidth(0.5)
  doc.setDrawColor(220, 220, 220)
  doc.line(15, 34, pageWidth - 15, 34)

  // Stats
  doc.setTextColor(0, 0, 0)
  doc.setFontSize(10)
  const totalEntradas = visitors.length
  const totalSaidas = visitors.filter(v => v.exit_time).length
  const aindaNoLocal = visitors.filter(v => !v.exit_time).length
  doc.text(`Total de Entradas: ${totalEntradas}  |  Saídas Registradas: ${totalSaidas}  |  Ainda no Local: ${aindaNoLocal}`, 14, 45)

  // Table
  const tableData = visitors.map(v => {
    const entry = v.entry_time ? format(new Date(v.entry_time), 'dd/MM HH:mm') : '-'
    const exit = v.exit_time ? format(new Date(v.exit_time), 'dd/MM HH:mm') : 'Ativo'
    let duracao = '-'
    if (v.entry_time) {
      const end = v.exit_time ? new Date(v.exit_time) : new Date()
      const mins = differenceInMinutes(end, new Date(v.entry_time))
      const h = Math.floor(mins / 60)
      const m = mins % 60
      duracao = `${h}h${m.toString().padStart(2, '0')}min`
    }
    const status = v.exit_time ? 'Concluído' : 'Ativo'
    return [v.name, v.document || '-', v.destination || '-', v.registered_by || '-', entry, exit, duracao, status]
  })

  autoTable(doc, {
    startY: 50,
    head: [['Visitante', 'Documento', 'Destino', 'Registrado por', 'Entrada', 'Saída', 'Permanência', 'Status']],
    body: tableData,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  })

  // Footer
  const pageCount = (doc as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(128, 128, 128)
    doc.text(`Gerado em ${format(new Date(), 'dd/MM/yyyy HH:mm')} - Página ${i}/${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' })
  }

  doc.save(`relatorio-visitantes-${startDate}-${endDate}.pdf`)
}
